import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import apiClient from '@/services/api';
import type { SalesSummaryReport } from '@/types/api';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Sales by Hour: revenue, order and cover counts bucketed by hour of day. */
export default function SalesByHourReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesSummaryReport>({
    queryKey: ['sales-summary', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesSummary(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const hours = (data?.byHour ?? []).map((h) => ({ ...h, revenue: Number(h.revenue) }));
  const hasData = (data?.totals?.orders ?? 0) > 0;
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    {
      heading: 'Sales by Hour',
      headers: ['Hour', 'Orders', 'Covers', 'Revenue'],
      rows: (data?.byHour ?? []).map((h) => [`${h.hour}:00`, h.orders, h.covers, formatCurrency(h.revenue)]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Orders', Number(data?.totals?.orders ?? 0).toLocaleString()],
    ['Covers', Number(data?.totals?.covers ?? 0).toLocaleString()],
    ['Revenue', formatCurrency(data?.totals?.total ?? 0)],
  ];

  return (
    <ReportFrame
      title="Sales by Hour"
      description="Revenue, orders and covers bucketed by hour of day across the range."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={!hasData}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`sales-by-hour-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Sales by Hour', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Sales by Hour Report', meta(), kpis(), sections())}
    >
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Revenue & Orders by Hour</h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={hours}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis yAxisId="rev" />
            <YAxis yAxisId="cnt" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="rev" dataKey="revenue" name="Revenue" fill="#0088FE" />
            <Line yAxisId="cnt" type="monotone" dataKey="orders" name="Orders" stroke="#FF8042" />
          </ComposedChart>
        </ResponsiveContainer>
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Hourly Breakdown</h3>
        <SimpleTable headers={['Hour', 'Orders', 'Covers', 'Revenue']}
          rows={(data?.byHour ?? []).map((h) => [`${h.hour}:00`, h.orders, h.covers, formatCurrency(h.revenue)])} />
      </div></div>
    </ReportFrame>
  );
}
